import React, { useEffect, useState } from "react";
import LoadingLogoPage from "../../loadingPage/loadingPage";
import { useAuth } from "../../../context/API_Client";
import EmptyState from "../../../reusableComponents/ConfigEmptyState/EmptyState";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import InviteUser from "./inviteUser";
import UserSearchField from "./usersSearchField";
import ContentHeader from "../../../reusableComponents/contentHeader/ContentHeader";
import BoxPayGrid from "../../BoxPayGrid";
import UsersSchema from "./UsersSchema";
import SmallModal from "../../../reusableComponents/modals/SmallModal";
import { customToast } from "../../../reusableComponents/customToaster/customToast";
import { convertIsoToGivenDateFormat } from "../../utils/utils";
import EditUser from "./editUser";
import { colorBrandObject } from "../../../../constants/brandings";

export default function Users({ operationPermissions }) {
  const {
    getAllModalData,
    getAllUsers,
    getAllRoles,
    deleteUserById,
    merchantSettings,
    resendInviteForUser,
  } = useAuth();
  const [usersData, setUsersData] = useState([]);
  const [updatedUsersData, setUpdatedUsersData] = useState([]);
  const [rolesData, setRolesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [toggleDialog, setToggleDialog] = useState(false);
  const [createUser, setCreateUser] = useState(false);
  const [filteringData, setFilteringData] = useState([]);
  const [resendData, setResendData] = useState([]);
  const [tableActionValue, setTableActionValue] = useState("");
  const [timeZone, setTimeZone] = useState(null);
  const [businessUnitData, setBusinessUnitData] = useState(null);

  useEffect(() => {
    merchantSettings(
      function (data) {
        setTimeZone(data?.timeZone);
      },
      function (errorMsg) { }
    );
    getAllModalData(
      function (data) {
        setBusinessUnitData(data?.legalEntities);
      },
      function (errorMsg) { }
    );
  }, []);
  useEffect(() => {
    if (
      !sessionStorage.getItem("manageUsers") ||
      sessionStorage.getItem("manageUsers") == 0
    ) {
      if (timeZone) {
        getAllUsers(
          (data) => {
            const filteredData = data?.map((rawData) => {
              const epochValue = rawData.updatedOn;
              const outputDatetimeStr = convertIsoToGivenDateFormat(
                epochValue,
                timeZone
              );
              rawData["updatedOn"] = outputDatetimeStr;
              const businessUnitNames = rawData?.businessUnits?.map(
                (unitCode) => {
                  return (
                    businessUnitData?.find(
                      (legalEntity) => legalEntity?.code === unitCode
                    )?.name || unitCode
                  );
                }
              );
              rawData["businessUnitsNames"] = businessUnitNames;
              return rawData;
            });
            setUsersData(filteredData);
          },
          (errorMsg) => {
            customToast("error", "high", errorMsg);
          }
        );
        getAllRoles(
          (data) => {
            const sortedRoles = data.sort((a, b) => a.name.localeCompare(b.name));
            setRolesData(sortedRoles);
          },
          (errorMsg) => {
            customToast("error", "high", errorMsg);
          }
        );
        setCreateUser(false);
      }
    }
  }, [createUser, timeZone , businessUnitData]);

  const handleClose = () => {
    setShowInviteModal(false);
  };
  const handleCloseEdit = () => {
    setShowEditModal(false);
    setEditing(false);
  };
  const actionValue = (value) => {
    setTableActionValue(value);
  };
  const editUserData = (value) => {
    setEditing(true);
    setEditId(value);
  };
  const deleteUserData = (deleteId) => {
    setDeleteId(deleteId?.email);
  };
  const handleUpdateUserData = (data) => {
    if (data) {
      const filteredData = data?.map((rawData) => {
        const epochValue = rawData.updatedOn;
        const outputDatetimeStr = convertIsoToGivenDateFormat(
          epochValue,
          timeZone
        );
        rawData["updatedOn"] = outputDatetimeStr;
        const businessUnitNames = rawData?.businessUnits?.map((unitCode) => {
          return (
            businessUnitData?.find(
              (legalEntity) => legalEntity?.code === unitCode
            )?.name || unitCode
          );
        });
        rawData["businessUnitsNames"] = businessUnitNames;
        return rawData;
      });
      const updatedUserData = data?.map((user) => {
        const roleId = user?.roles?.[0];
        const matchingRole = rolesData?.find((role) => role?.id === roleId);

        return {
          ...user,
          rolesName: matchingRole?.name || null,
        };
      });
      setFilteringData(updatedUserData);
    }
  };
  useEffect(() => {
    if (deleteId && tableActionValue === "Delete" && editing) {
      setToggleDialog(true);
    }
  }, [deleteId, tableActionValue, editing]);
  const resendInvite = (data) => {
    setResendData(data);
  };

  useEffect(() => {
    if (editing && tableActionValue === "Edit User" && editId) {
      setShowEditModal(true);
    }
  }, [editing, tableActionValue, editId]);
  useEffect(() => {
    if (resendData && tableActionValue === "Resend Invite") {
      resendInviteForUser(
        resendData?.email,
        function (data) {
          customToast("success", "high", "Inivite resend successfully");
        },
        function (errMsg) {
          customToast("error", "high", errMsg);
        }
      );
    }
  }, [resendData, tableActionValue]);

  const handleSearchData = (attrName, values) => {
    if (values?.length) {
      const filteredData = updatedUsersData.filter((user) => {
        const attributeValue = user[attrName].toLowerCase();
        return attributeValue.includes(values.toLowerCase());
      });
      setFilteringData(filteredData);
    } else {
      const updatedUserData = usersData?.map((user) => {
        const roleId = user?.roles?.[0];
        const matchingRole = rolesData?.find((role) => role?.id === roleId);

        return {
          ...user,
          rolesName: matchingRole?.name || null,
        };
      });
      setFilteringData(updatedUserData);
    }
  };

  const handleDeleteToggleBtn = () => {
    deleteUserById(
      deleteId,
      function (data) {
        setDeleteId(null);
        getAllUsers(
          (data) => {
            const filteredData = data?.map((rawData) => {
              const epochValue = rawData.updatedOn;
              const outputDatetimeStr = convertIsoToGivenDateFormat(
                epochValue,
                timeZone
              );
              rawData["updatedOn"] = outputDatetimeStr;
              const businessUnitNames = rawData?.businessUnits?.map(
                (unitCode) => {
                  return (
                    businessUnitData?.find(
                      (legalEntity) => legalEntity?.code === unitCode
                    )?.name || unitCode
                  );
                }
              );
              rawData["businessUnitsNames"] = businessUnitNames;
              return rawData;
            });
            setUsersData(data);
            setFilteringData(data);
            setToggleDialog(false);
            customToast("success", "high", `User deleted successfully`);
          },
          (errorMsg) => {
            customToast("error", "high", errorMsg);
          }
        );
      },
      function (errMsg) {
        customToast("error", "high", errMsg);
      }
    );
  };

  useEffect(() => {
    const updatedUserData = usersData?.map((user) => {
      const roleId = user?.roles?.[0];
      const matchingRole = rolesData?.find((role) => role?.id === roleId);

      return {
        ...user,
        rolesName: matchingRole?.name || null,
      };
    });
    setUpdatedUsersData(updatedUserData);
    setFilteringData(updatedUserData);
  }, [usersData, rolesData]);

  useEffect(() => {
    if (usersData?.length) {
      setLoading(false);
    }
  }, [usersData]);

  return (
    <>
      {loading && usersData?.length === 0 ? (
        <div>
          <section
            className="content"
            style={{
              top: "50%",
              left: "50%",
              backgroundColor: "white",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <LoadingLogoPage />
          </section>
        </div>
      ) : (
        <section className="mainDivWebHooks">
          {usersData?.length > 0 ? (
            <>
              <ContentHeader
                firstHeading="Invite Your Team to BoxPay"
                secondHeading="Extend invitations to your team members, granting them access to BoxPay's collaborative dashboard"
                buttonPermissions={[operationPermissions?.createUser]}
                icon={<GroupAddOutlinedIcon sx={{color:colorBrandObject.sideBarSelectedOptionBackground}} />}
                buttonText="Invite User"
                buttonType="primary"
                buttonSize="md"
                buttonVariant="contained"
                buttonOnclick={() => setShowInviteModal(true)}
              />
              <UserSearchField searchData={handleSearchData} />
              <div
                style={{ width: "100%", display: "flex", marginTop: "32px" }}
              >
                <BoxPayGrid
                  schema={UsersSchema(
                    deleteUserData,
                    editUserData,
                    resendInvite,
                    actionValue,
                    operationPermissions
                  )}
                  data={filteringData}
                  showCustomizeButton={false}
                />
              </div>
              <SmallModal
                headerText={"Delete User"}
                subHeaderText={
                  "Are you sure you want to delete this user? The user will not be able to access the platform thereafter."
                }
                outlinedBtnText={"Cancel"}
                filledBtnText={"Delete"}
                outlinedBtnFunction={() => {
                  setToggleDialog(false);
                  setEditing(false);
                }}
                filledBtnFunction={handleDeleteToggleBtn}
                toggleDialog={toggleDialog}
                icon={<img src="/assets/routingIcons/ExclamationCircle.svg" />}
              />
            </>
          ) : (
            <div
              style={{
                backgroundColor: "white",
                width: "100%",
                borderRadius: "16px",
                border:
                  "1.2px solid var(--border-tertiary, rgba(1, 1, 2, 0.12))",
              }}
            >
              <EmptyState
                image={
                  <GroupAddOutlinedIcon
                    sx={{
                      color: "rgba(201, 201, 204, 1)",
                      height: "120px",
                      width: "120px",
                    }}
                  />
                }
                mainText={"Invite team members"}
                subText={"Please invite team members to colloborate"}
                btnText={"Invite User"}
                btnFunction={() => setShowInviteModal(true)}
                btnPermission={operationPermissions?.createUser}
              />
            </div>
          )}
        </section>
      )}
      <InviteUser
        open={showInviteModal}
        onClose={handleClose}
        createUser={createUser}
        setCreateUser={setCreateUser}
        rolesData={rolesData}
        editing={editing}
        editId={editId}
        tableActionValue={tableActionValue}
      />
      <EditUser
        open={showEditModal}
        onClose={handleCloseEdit}
        createUser={createUser}
        rolesData={rolesData}
        editId={editId}
        tableActionValue={tableActionValue}
        handleUpdateUserData={handleUpdateUserData}
      />
    </>
  );
}
